import { useState } from 'react';
import { submitAlumniRegistration } from '../lib/api.js';

const EMPTY = {
  firstName: '', lastName: '', email: '', phone: '', studentId: '', dateOfBirth: '',
  addressRoad: '', addressArea: '', addressState: '', addressCountry: '',
  joiningYear: '', joiningClass: '', yearLeft: '', classLeft: '',
  leaveReason: '', message: '',
};

const LEAVE_REASONS = ['Graduated', 'Transferred Out', 'N/A'];
const THIS_YEAR = new Date().getFullYear();

/**
 * Public self-registration form for former students. Posts to
 * /api/alumni-registration, which stores the submission in its own
 * `alumni_registrations` table and emails the school.
 */
export function AlumniRegistrationForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const busy = status === 'sending';

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      await submitAlumniRegistration(form);
      setStatus('ok');
      setForm(EMPTY);
      window.scrollTo({ top: document.querySelector('.alumni-reg-form')?.offsetTop - 120 || 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err.message || 'Registration failed';
      setError(msg);
      setStatus('error');
    }
  };

  if (status === 'ok') {
    return (
      <div className="alumni-reg-done">
        <h2>Thank you — you're registered.</h2>
        <p>
          We've received your details and added you to the Maple Leaf alumni network.
          Someone from the school will be in touch about events, mentoring and reunions.
        </p>
        <button type="button" className="btn btn-ghost" onClick={() => setStatus('idle')}>
          Register someone else
        </button>
      </div>
    );
  }

  return (
    <form className="form-grid alumni-reg-form" style={{ marginTop: 32 }} onSubmit={onSubmit}>
      <h3 className="full alumni-reg-legend">Your details</h3>

      <div className="field">
        <label htmlFor="ar-first">First name *</label>
        <input id="ar-first" type="text" required value={form.firstName} onChange={change('firstName')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-last">Last name *</label>
        <input id="ar-last" type="text" required value={form.lastName} onChange={change('lastName')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-email">Email address *</label>
        <input id="ar-email" type="email" required value={form.email} onChange={change('email')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-phone">Phone</label>
        <input id="ar-phone" type="tel" value={form.phone} onChange={change('phone')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-sid">Student ID</label>
        <input id="ar-sid" type="text" value={form.studentId} onChange={change('studentId')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-dob">Date of birth</label>
        <input id="ar-dob" type="date" value={form.dateOfBirth} onChange={change('dateOfBirth')} disabled={busy} />
      </div>

      <h3 className="full alumni-reg-legend">Present address *</h3>
      <div className="field">
        <label htmlFor="ar-road">Road *</label>
        <input id="ar-road" type="text" required value={form.addressRoad} onChange={change('addressRoad')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-area">Area *</label>
        <input id="ar-area" type="text" required value={form.addressArea} onChange={change('addressArea')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-state">State / Division *</label>
        <input id="ar-state" type="text" required value={form.addressState} onChange={change('addressState')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-country">Country *</label>
        <input id="ar-country" type="text" required value={form.addressCountry} onChange={change('addressCountry')} disabled={busy} />
      </div>

      <h3 className="full alumni-reg-legend">Your time at Maple Leaf</h3>
      <div className="field">
        <label htmlFor="ar-jyear">Joining year *</label>
        <input
          id="ar-jyear" type="number" required min="1950" max={THIS_YEAR + 1} placeholder="e.g. 2008"
          value={form.joiningYear} onChange={change('joiningYear')} disabled={busy}
        />
      </div>
      <div className="field">
        <label htmlFor="ar-jclass">Joining class *</label>
        <input id="ar-jclass" type="text" required placeholder="e.g. Class III" value={form.joiningClass} onChange={change('joiningClass')} disabled={busy} />
      </div>
      <div className="field">
        <label htmlFor="ar-lyear">Year left *</label>
        <input
          id="ar-lyear" type="number" required min="1950" max={THIS_YEAR + 1} placeholder="e.g. 2018"
          value={form.yearLeft} onChange={change('yearLeft')} disabled={busy}
        />
      </div>
      <div className="field">
        <label htmlFor="ar-lclass">Class left</label>
        <input id="ar-lclass" type="text" placeholder="e.g. IAL" value={form.classLeft} onChange={change('classLeft')} disabled={busy} />
      </div>
      <div className="field full">
        <label htmlFor="ar-reason">How did you leave MLIS? *</label>
        <select id="ar-reason" required value={form.leaveReason} onChange={change('leaveReason')} disabled={busy}>
          <option value="" disabled>Please choose…</option>
          {LEAVE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="field full">
        <label htmlFor="ar-message">Tell us what you've been up to!</label>
        <textarea id="ar-message" rows={5} value={form.message} onChange={change('message')} disabled={busy} />
      </div>

      <div className="full" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Submitting…' : 'Submit registration'}
        </button>
        <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>Fields marked * are required.</span>
        {status === 'error' && <span style={{ color: '#b54033', fontSize: 14 }}>{error}</span>}
      </div>
    </form>
  );
}
